import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { authTokenVault } from "@/shared/api/authTokenVault";
import { activeOrganizationVault } from "@/shared/api/activeOrganizationVault";
import { refreshBrowserAccessToken } from "@/shared/api/browserApiClient";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "")
  .trim()
  .replace(/\/+$/, "");

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  credentials: "include",
  prepareHeaders: (headers) => {
    const accessToken = authTokenVault.get();

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const activeOrganizationId = activeOrganizationVault.get();
    if (accessToken && activeOrganizationId) {
      headers.set("X-Merry-Organization-ID", activeOrganizationId);
    }

    return headers;
  },
  timeout: 30_000,
});

const publicAuthEndpoints = new Set([
  "codeLogin",
  "logout",
  "passwordLogin",
  "invitationRegister",
  "refreshSession",
  "register",
  "resetPassword",
  "sendPhoneCode",
]);

function isOrganizationScopeDenied(error: FetchBaseQueryError | undefined) {
  if (error?.status !== 403 || typeof error.data !== "object" || !error.data) {
    return false;
  }
  return "error" in error.data && error.data.error === "organization_scope_denied";
}

const baseQueryWithReauthentication: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (
    isOrganizationScopeDenied(result.error) &&
    activeOrganizationVault.get() &&
    !publicAuthEndpoints.has(api.endpoint)
  ) {
    activeOrganizationVault.clear();
    result = await rawBaseQuery(args, api, extraOptions);
  }

  if (result.error?.status !== 401 || publicAuthEndpoints.has(api.endpoint)) {
    return result;
  }

  if (await refreshBrowserAccessToken()) {
    result = await rawBaseQuery(args, api, extraOptions);
  } else {
    activeOrganizationVault.clear();
    api.dispatch({ type: "authSession/sessionExpired" });
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauthentication,
  endpoints: () => ({}),
  tagTypes: [
    "CapabilityReadiness",
    "BillingOverview",
    "BillingProducts",
    "BillingTransactions",
    "BillingOrder",
    "AdvertisingAccounts",
    "AdvertisingRechargeOptions",
    "AdvertisingAccountActivity",
    "AdvertisingQuote",
    "MeSettings",
    "Organization",
    "UserPreferences",
    "MeAvatar",
    "NotificationPreferences",
    "Notifications",
    "NotificationSummary",
    "HelpSupportEntries",
    "MerchantStoreBindings",
    "MerchantContext",
    "MerchantAdAccountBindings",
    "TeamMembers",
    "TeamInvitation",
    "ScheduledTasks",
    "SourceSyncAuthorizations",
    "CloudResources",
    "CloudResource",
    "CloudArchive",
    "Conversation",
    "ConversationMessages",
    "ConversationTurns",
    "ConversationExecutionNodes",
    "ConversationWorkflows",
    "ConversationFreshTasks",
    "Conversations",
    "CreativeMediaJob",
    "CreativeMediaSelection",
    "ResourceAvailability",
    "ExperienceStrategyProposal",
    "ExperienceStrategyTimeline",
    "ExperienceStrategyCurrent",
    "ExperienceStrategyDetail",
  ],
});
